import { addDays } from "./date";
import { dayKcal } from "./energy";
import type { KayaState } from "./types";

/**
 * The arithmetic behind "will this plan get me there".
 *
 * This file answers one narrow question: how much of the gap does the exercise itself account
 * for. It predates food logging (ADR-025) and stays as the fallback for when food is off or not
 * yet logged — the in-vs-out answer, and the prediction built on both sides, lives in
 * `ledger.ts`, and the finish date comes from the weight trend in `trend.ts`.
 *
 * 7,700 kcal per kg of fat is the usual figure, and the one constant every conversion uses. It is an approximation — real loss includes water
 * and lean tissue and the body adapts as it gets lighter — so it answers "roughly how long", not
 * "which day".
 */
export const KCAL_PER_KG_FAT = 7700;

export interface GapToTarget {
  kgToLose: number;
  kcalTotal: number;
}

export function gapToTarget(S: KayaState, currentKg: number): GapToTarget | null {
  const target = S.profile.targetWeight;
  if (S.profile.goal !== "weight" || !target || !(currentKg > target)) return null;
  const kgToLose = currentKg - target;
  return { kgToLose, kcalTotal: Math.round(kgToLose * KCAL_PER_KG_FAT) };
}

/** Mean daily burn over the window, counting only days that actually logged something. */
export function averageDailyKcal(S: KayaState, today: string, days = 14): number {
  let sum = 0;
  let counted = 0;
  for (let i = 0; i < days; i++) {
    const k = dayKcal(S, addDays(today, -i));
    if (k > 0) {
      sum += k;
      counted++;
    }
  }
  return counted ? Math.round(sum / counted) : 0;
}

/** Kilograms a month that this much daily exercise accounts for, on its own. */
export function kgPerMonthFromExercise(kcalPerDay: number): number {
  return (kcalPerDay * 30) / KCAL_PER_KG_FAT;
}

/**
 * Days to the target if exercise were the only thing creating a deficit.
 *
 * Presented as an upper bound to argue against, not a forecast: it assumes eating stays exactly
 * as it is, which is the assumption that usually fails.
 */
export function exerciseOnlyDays(gap: GapToTarget, kcalPerDay: number): number | null {
  if (kcalPerDay <= 0) return null;
  return Math.round(gap.kcalTotal / kcalPerDay);
}

/** 0.25–0.5 kg a week is the range usually considered sustainable. */
export const SUSTAINABLE_KG_PER_WEEK = { low: 0.25, high: 0.5 };

export function weeksAtSustainableRate(gap: GapToTarget): { fast: number; slow: number } {
  return {
    fast: Math.round(gap.kgToLose / SUSTAINABLE_KG_PER_WEEK.high),
    slow: Math.round(gap.kgToLose / SUSTAINABLE_KG_PER_WEEK.low),
  };
}
